import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonFunctions } from '@shared/common-functions';
import { DEFAULT_LOGO, USER_ROLES } from '@shared/constant/shared.constant';
import { RevenueDashboardResponse, ParkingLot } from '@core/models/api-response.model';
import { CommonMenuService } from '@shared/services/common-util.service';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { MenuItem } from 'primeng/api';
import { SuperAdminService } from '@core/services/super-admin.service';
import { debounceTime, Subject } from 'rxjs';
import { DatePipe } from '@angular/common';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

@Component({
  selector: 'app-revenue-dashboard',
  templateUrl: './revenue-dashboard.component.html',
  styleUrls: ['./revenue-dashboard.component.scss'],
})
export class RevenueDashboardComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  @Input() dateValidationError: boolean = false;
  @Input() set selectedCompany(value: any) {
    this.companyId = value;
    this.triggerApiCall();
  }

  @Input() set selectedParkingLot(value: any) {
    this.selectedParkingLotIds = value;
    this.triggerApiCall();
  }

  @Input() set checkStartDate(value: any) {
    this.startDate = value;
  }
  @Input() set checkEndDate(value: any) {
    this.endDate = value;
  }

  @Input() set searchClick(_value: any) {
    this.triggerApiCall();
  }
  private readonly changesSubject = new Subject<void>(); // Event trigger
  startDate: any;
  endDate: any;
  activeItem: any;
  items: MenuItem[] = [];
  companyList: { name: string; value: string }[];
  parkingLot: ParkingLot[] = [];
  companyDropdownOptions: { name: string; value: string }[];
  parkingLotOptions: ParkingLot[] = [];
  pageNumber = 1;
  first = 0;
  revenueCollectionData: RevenueDashboardResponse;
  dummy = DEFAULT_LOGO;
  companyId: string;
  selectedParkingLotIds: [];
  paymentCardsData;
  userRoles = USER_ROLES;
  private readonly debounceTimeInMs = 300;

  constructor(
    private readonly service: SuperAdminService,
    private readonly router: Router,
    private readonly datePipe: DatePipe,
    private readonly commonMenuService: CommonMenuService,
    private readonly encryptStorage: EncryptionStorageService
  ) {}

  ngOnInit(): void {
    this.getRevenueCollectionDashboard();
    this.changesSubject.pipe(debounceTime(this.debounceTimeInMs)).subscribe(() => {
      this.pageNumber=1;
      this.getRevenueCollectionDashboard();
    });
  }

  private triggerApiCall() {
    this.changesSubject.next();
    this.first = 0;
  }

  showData(rowData: any) {
    const config = [
      {
        label: '',
        icon: '',
      },
      {
        label: 'View Details',
        icon: 'pi pi-eye',
        command: () => this.viewTicket(rowData),
      },
    ];
    this.items = this.commonMenuService.commonShowData(config);
  }

  viewTicket(rowData: any): void {
    const roleName = this.encryptStorage.getItem('roleName');
    const basePath = roleName === this.userRoles.SUPER_ADMIN ? '/super-admin' : '/manager';
    this.router.navigate(
      [`${basePath}/revenue-collection-dashboard/overview`],
      { state: { rowData, startDate: this.startDate, endDate: this.endDate } }
    );
  }

  getRevenueCollectionDashboard(): void {
    
    const companyIds = this.companyId ? [this.companyId] : null;
    const parkingLotIds = this.selectedParkingLotIds? [this.selectedParkingLotIds]: null;
    const req = {
      companyIds: companyIds,
      parkingLotIds: parkingLotIds,
      dateRange: null,
      timeZone: Utility.getLocalTimeZone(),
      startDate: CommonFunctions.formatDate(this.startDate, this.datePipe),
      endDate: CommonFunctions.formatDate(this.endDate, this.datePipe),
      pageNumber: this.pageNumber,
      pageSize: 10,
    };

    this.service
      .getRevenueCollectionDashboard(req)
      .pipe(
        autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.revenueCollectionData = response;
          this.updatePaymentCardsData(this.revenueCollectionData);
        },
        error: () => {
          if (this.revenueCollectionData) {
            this.revenueCollectionData.data = [];
          }
          const revenueCollectionData = {
            paymentPendingBookingCount: 0,
            paymentPending: 0,
            paymentFailedBookingCount: 0,
            paymentFailed: 0,
            customInvoicePaymentPendingCount: 0,
            customInvoicePaymentPending: 0,
            customInvoicePaymentFailedCount: 0,
            customInvoicePaymentFailed: 0,
          };
          this.updatePaymentCardsData(revenueCollectionData);
        },
      });
  }

  private updatePaymentCardsData(data: any): void {
    this.paymentCardsData = [
      {
        image: 'assets/images/booking-icons.svg',
        label: 'Bookings',
        pCount: data?.paymentPendingBookingCount,
        fCount: data?.paymentFailedBookingCount,
        pTitle: 'Pending Payments',
        fTitle: 'Failed Payments',
        pAmount: data?.paymentPending,
        fAmount: data?.paymentFailed,
      },
      {
        image: 'assets/images/custom-invoice-icon.svg',
        label: 'Custom <br/> Invoices',
        pCount: data?.customInvoicePaymentPendingCount,
        fCount: data?.customInvoicePaymentFailedCount,
        pTitle: 'Pending Payments',
        fTitle: 'Failed Payments',
        pAmount: data?.customInvoicePaymentPending,
        fAmount: data?.customInvoicePaymentFailed,
      },
    ];
  }

  onPageChange(event) {
    this.first = event.first;
    this.pageNumber = event.page + 1;
    CommonFunctions.scrollTopRow('top');
    this.getRevenueCollectionDashboard();
  }
}
